const THEME_MODE_STORAGE_KEY = 'theme-mode';

const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

const THEME_MODE_VALUES = new Set(Object.values(THEME_MODES));

function getSystemThemeMode () {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return THEME_MODES.LIGHT;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEME_MODES.DARK
    : THEME_MODES.LIGHT;
}

function getStoredThemeMode () {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return THEME_MODES.SYSTEM;
  }

  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return THEME_MODE_VALUES.has(storedMode) ? storedMode : THEME_MODES.SYSTEM;
}

function resolveThemeMode (mode) {
  return mode === THEME_MODES.SYSTEM ? getSystemThemeMode() : mode;
}

function applyResolvedThemeMode (resolvedMode) {
  if (typeof document === 'undefined') {
    return;
  }

  const htmlElement = document.documentElement;
  htmlElement.setAttribute('data-theme', resolvedMode);
  htmlElement.style.colorScheme = resolvedMode;
}

function applyThemeMode (mode, options = {}) {
  const { persist = true } = options;
  const safeMode = THEME_MODE_VALUES.has(mode) ? mode : THEME_MODES.SYSTEM;
  const resolvedMode = resolveThemeMode(safeMode);

  applyResolvedThemeMode(resolvedMode);

  if (persist && typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, safeMode);
  }

  return {
    mode: safeMode,
    resolvedMode
  };
}

function initThemeMode () {
  const storedMode = getStoredThemeMode();
  return applyThemeMode(storedMode, { persist: false });
}

function bindSystemThemeListener () {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => {
    if (getStoredThemeMode() === THEME_MODES.SYSTEM) {
      applyThemeMode(THEME_MODES.SYSTEM, { persist: false });
    }
  };

  mediaQuery.addEventListener('change', handleChange);

  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
}

export {
  THEME_MODE_STORAGE_KEY,
  THEME_MODES,
  applyThemeMode,
  bindSystemThemeListener,
  getStoredThemeMode,
  initThemeMode,
  resolveThemeMode
};
